import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Skeleton, Switch, Tooltip, Typography } from '@mui/material';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import { colors } from '../../styles/theme';
import { toast } from '../../Components/Toast';
import { confirmDialog } from '../../Components/ConfirmDialog';
import { getMetaWriteSettings, updateMetaWriteSettings } from '../../services/metaAdsService';
import {
  META_WRITE_KILL_SWITCH_MESSAGE, metaWriteAccountView, readMetaWriteAccount, readMetaWriteError, readMetaWriteSettings,
} from '../metaWrite';
import { metaAdsMutedTextSx, metaAdsSkeletonSx } from './metaAdsUi.styles';

/*
 * F2a · Fila «Acciones en Meta» de ⚙ Configuración de Meta. Solo se monta con el bloque `write`
 * de getmetaconnection (sin él, ⚙ queda como antes). Pide `getmetawritesettings` al abrir ⚙.
 * - Sin permiso de escritura: dueño → «Habilitar» (login con escritura); editor → solo el estado.
 * - Con permiso: un interruptor por cuenta (dueño; habilitar pide confirmación y avisa del gasto
 *   real); el editor ve el estado sin interruptores.
 * - Interruptor de emergencia: el aviso y nada más.
 */

const rowSx = {
  display: 'grid', gridTemplateColumns: '20px minmax(0, 1fr) auto', columnGap: 1, alignItems: 'start', py: 1.25,
  borderTop: '1px solid rgba(255,255,255,0.06)',
};
const lineSx = { fontSize: '13px', lineHeight: '18px', color: colors.textPanelTitle, fontWeight: 600 };
const subSx = { ...metaAdsMutedTextSx, fontSize: '12px', lineHeight: '16px' };
const actionSx = { textTransform: 'none', fontWeight: 600, px: 0.75, py: 0, minWidth: 0, fontSize: '12.5px', color: colors.primaryLight };
const switchSx = {
  '& .MuiSwitch-switchBase.Mui-checked': { color: '#FFFFFF' },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: colors.primary, opacity: 1 },
  '& .MuiSwitch-track': { bgcolor: '#5A5A5A', opacity: 1 },
  '& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track': { opacity: 0.5 },
};

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

export function metaWriteEnableText(account) {
  return 'Vas a poder modificar sus campañas desde Terrio.';
}
export const META_WRITE_REAL_MONEY_ENABLE_TEXT = 'Reactivar o subir presupuestos gasta plata real.';

/**
 * @param {{ connectionWrite: object, ownerUserId?: string|null, canManage: boolean, connecting?: boolean,
 *   onReconnectWrite?: (() => void)|null, onChanged?: (() => void)|null }} props
 * «Historial de cambios» es una fila propia de ⚙ (`MetaSettingsButton` `onOpenLog`).
 */
export default function MetaWriteSettingsRow({
  connectionWrite, ownerUserId = null, canManage, connecting = false, onReconnectWrite = null, onChanged = null,
}) {
  const [state, setState] = useState({ status: 'loading', settings: null });
  const [saving, setSaving] = useState('');
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const scopeGranted = Boolean(connectionWrite?.scopeGranted);
  const killSwitch = Boolean(connectionWrite?.killSwitch);

  useEffect(() => {
    if (!scopeGranted || killSwitch) return undefined;
    const controller = new AbortController();
    getMetaWriteSettings({ ownerUserId, signal: controller.signal }).then((res) => {
      if (controller.signal.aborted || !mountedRef.current) return;
      setState(res?._httpOk ? { status: 'ready', settings: readMetaWriteSettings(res) } : { status: 'error', settings: null });
    }).catch((e) => {
      if (e?.name !== 'AbortError' && mountedRef.current) setState({ status: 'error', settings: null });
    });
    return () => controller.abort();
  }, [ownerUserId, scopeGranted, killSwitch]);

  const replaceAccount = useCallback((row) => {
    setState((prev) => (prev.settings ? {
      ...prev,
      settings: { ...prev.settings, accounts: prev.settings.accounts.map((a) => (a.id === row.id ? row : a)) },
    } : prev));
  }, []);

  const toggle = async (account, enabled) => {
    if (saving) return;
    if (enabled) {
      const { isConfirmed } = await confirmDialog({
        title: `¿Habilitar «${account.name}»?`,
        description: account.realMoney ? `${metaWriteEnableText(account)} ${META_WRITE_REAL_MONEY_ENABLE_TEXT}` : metaWriteEnableText(account),
        confirmLabel: 'Habilitar',
        variant: account.realMoney ? 'warning' : 'info',
      });
      if (!isConfirmed || !mountedRef.current) return;
    }
    setSaving(account.id);
    const res = await updateMetaWriteSettings({ adAccountId: account.id, enabled });
    if (!mountedRef.current) return;
    setSaving('');
    if (!res?._httpOk) {
      const err = readMetaWriteError(res);
      toast.error({ title: 'No pudimos guardar el cambio', message: err.message });
      return;
    }
    const row = readMetaWriteAccount(res.data);
    if (row) replaceAccount(row);
    toast.success({ message: enabled ? `«${account.name}» habilitada.` : `«${account.name}» deshabilitada.` });
    onChanged?.();
  };

  let body;
  let action = <span />;
  if (killSwitch) {
    body = <Typography sx={subSx} data-testid="meta-write-settings-kill-switch">{META_WRITE_KILL_SWITCH_MESSAGE}</Typography>;
  } else if (!scopeGranted) {
    body = (
      <Typography sx={subSx} data-testid="meta-write-settings-no-scope">
        {canManage
          ? 'Modificá tus campañas desde Terrio.'
          : 'Solo lectura'}
      </Typography>
    );
    if (canManage && onReconnectWrite) {
      action = (
        <Button size="small" onClick={onReconnectWrite} disabled={connecting} sx={actionSx} data-testid="meta-write-settings-enable">
          {connecting ? 'Conectando…' : 'Habilitar'}
        </Button>
      );
    }
  } else if (state.status === 'loading') {
    body = <Skeleton variant="rounded" width="70%" height={14} sx={{ ...metaAdsSkeletonSx, mt: 0.25 }} />;
  } else if (state.status === 'error') {
    body = <Typography sx={subSx}>No pudimos cargar las cuentas.</Typography>;
  } else {
    const accounts = state.settings.accounts;
    const enabledCount = accounts.filter((a) => a.enabled).length;
    body = (
      <>
        <Typography sx={subSx} data-testid="meta-write-settings-count">
          {canManage
            ? `${enabledCount} de ${plural(accounts.length, 'cuenta habilitada', 'cuentas habilitadas')}`
            : (enabledCount ? `${plural(enabledCount, 'cuenta habilitada', 'cuentas habilitadas')} · Solo lectura` : 'Solo lectura')}
        </Typography>
        {canManage ? (
          <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, mt: 0.5 }}>
            {accounts.map((a) => {
              const view = metaWriteAccountView(a);
              return (
                <Box component="li" key={a.id} sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', columnGap: 1, alignItems: 'center', py: 0.5 }} data-testid={`meta-write-account-${a.id}`}>
                  <Typography sx={{ fontSize: '13px', lineHeight: '18px', color: view.kind === 'unavailable' ? colors.textSecondary : '#D4D4D4', overflowWrap: 'anywhere', minWidth: 0 }}>
                    {a.currency ? `${a.name} (${a.currency})` : a.name}
                  </Typography>
                  {view.kind === 'switch' ? (
                    <Tooltip title={view.tooltip ? <span>{view.tooltip}</span> : ''} placement="left" arrow>
                      <span>
                        <Switch
                          size="small"
                          checked={a.enabled}
                          disabled={!view.toggleable || Boolean(saving)}
                          onChange={(e) => toggle(a, e.target.checked)}
                          inputProps={{ 'aria-label': `Acciones en Meta para ${a.name}`, 'data-testid': `meta-write-account-${a.id}-switch` }}
                          sx={switchSx}
                        />
                      </span>
                    </Tooltip>
                  ) : view.kind === 'reconnect' && onReconnectWrite ? (
                    <Tooltip title={<span>{view.tooltip}</span>} placement="left" arrow>
                      <Button size="small" onClick={onReconnectWrite} disabled={connecting} sx={actionSx} data-testid={`meta-write-account-${a.id}-reconnect`}>Reconectar</Button>
                    </Tooltip>
                  ) : view.kind === 'unavailable' ? (
                    <Tooltip title={<span>{view.tooltip}</span>} placement="left" arrow>
                      <Typography component="span" tabIndex={0} sx={{ fontSize: '12px', lineHeight: '18px', color: colors.textSecondary, whiteSpace: 'nowrap', cursor: 'default' }} data-testid={`meta-write-account-${a.id}-unavailable`}>
                        No disponible
                      </Typography>
                    </Tooltip>
                  ) : <span />}
                </Box>
              );
            })}
          </Box>
        ) : null}
      </>
    );
  }

  return (
    <Box sx={rowSx} data-testid="meta-settings-write">
      <EditNoteOutlinedIcon sx={{ fontSize: 18, mt: '1px', color: colors.textSecondary }} aria-hidden />
      <Box sx={{ minWidth: 0, gridColumn: scopeGranted && !killSwitch && canManage ? '2 / 4' : undefined }}>
        <Typography sx={lineSx}>Acciones en Meta</Typography>
        {body}
      </Box>
      {scopeGranted && !killSwitch && canManage ? null : action}
    </Box>
  );
}
